import React, { useLayoutEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchPurchaseList, invalidatePurchaseList } from '../store/slices/purchaseSlice';
import {
  OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE,
  useSelectedOperatingGroup,
} from '../hooks/useSelectedOperatingGroup';
import api, { OPERATING_GROUP_CHANGED_EVENT, OPERATING_GROUP_STORAGE_KEY } from '../api/api';
import * as XLSX from 'xlsx';
import { isOperatingContextReady, useOperatingContext } from '../utils/operatingContextLifecycle';

type ImportResult = {
  imported: number;
  updated?: number;
  errors: string[];
  rolledBack?: boolean;
  files?: Array<{ fileName: string; type: string; imported: number; updated: number; errors: string[] }>;
};

type ImportType =
  | 'products'
  | 'semifinished'
  | 'dishes'
  | 'product-categories'
  | 'semifinished-categories'
  | 'dish-categories'
  | 'dish-template';

type ImportPreview = {
  sheetName: string;
  headers: string[];
  sampleRows: unknown[][];
  totalRows: number;
  error?: string;
  serverValidation?: {
    sheetName: string;
    totalRows: number;
    validRows: number;
    invalidRows: number;
    errors: string[];
    errorsTruncated: boolean;
  };
  serverValidationError?: string;
};
const sectionStyle: React.CSSProperties = {
  background: 'var(--crm-surface)',
  color: 'var(--crm-text)',
  border: '1px solid var(--crm-border)',
  padding: '20px',
  borderRadius: 'var(--crm-radius-md)',
  boxShadow: 'var(--crm-shadow-sm)',
  marginBottom: '24px',
};

const buttonStyle: React.CSSProperties = {
  padding: '10px 20px',
  color: 'var(--crm-info)',
  background: 'var(--crm-info-bg)',
  border: '1px solid var(--crm-info)',
  borderRadius: 'var(--crm-radius-sm)',
  cursor: 'pointer',
};

const neutralButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  color: 'var(--crm-text)',
  background: 'var(--crm-surface-alt)',
  borderColor: 'var(--crm-border)',
};

const successButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  color: 'var(--crm-success)',
  background: 'var(--crm-success-bg)',
  borderColor: 'var(--crm-success)',
};

const controlStyle: React.CSSProperties = {
  display: 'block',
  padding: '6px',
  marginTop: '4px',
  border: '1px solid var(--crm-border)',
  borderRadius: 'var(--crm-radius-sm)',
  background: 'var(--crm-input-bg)',
  color: 'var(--crm-text)',
};

const ExportImportPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { purchaseList, loading, error: purchaseError } = useAppSelector((s) => s.purchase);
  const { groupId, changedInAnotherTab } = useSelectedOperatingGroup();
  const context = useOperatingContext();
  const token = useAppSelector(s => s.auth.token);
  const contextReady = isOperatingContextReady(context, token);
  const purchaseRequest = useRef<{ abort: () => void } | null>(null);

  useLayoutEffect(() => {
    const invalidate = () => {
      dispatch(invalidatePurchaseList());
      purchaseRequest.current?.abort();
      purchaseRequest.current = null;
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === OPERATING_GROUP_STORAGE_KEY && event.newValue !== groupId) invalidate();
    };
    invalidate();
    // Invalidate every event, even if React batches A-to-B-to-A into the same render.
    window.addEventListener(OPERATING_GROUP_CHANGED_EVENT, invalidate);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(OPERATING_GROUP_CHANGED_EVENT, invalidate);
      window.removeEventListener('storage', onStorage);
      invalidate();
    };
  }, [dispatch, groupId, changedInAnotherTab, context.generation, contextReady]);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [importFile, setImportFile] = useState<File | null>(null);
  const [importType, setImportType] = useState<ImportType>('products');
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const previewSequence = useRef(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const [packageFiles, setPackageFiles] = useState<File[]>([]);
  const [packageResult, setPackageResult] = useState<ImportResult | null>(null);

  const [docsResult, setDocsResult] = useState<ImportResult | null>(null);
  const [clearBeforeDocsImport, setClearBeforeDocsImport] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = async (type: string) => {
    setExportError(null);
    try {
      const response = await api.get(`/export/${type}`, { responseType: 'blob' });
      const disposition = response.headers?.['content-disposition'] as string | undefined;
      const match = disposition?.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
      const fileName = match?.[1] ? decodeURIComponent(match[1]) : `${type.replace(/\//g, '-')}.xlsx`;
      const contentTypeHeader = response.headers?.['content-type'];
      const contentType = typeof contentTypeHeader === 'string'
        ? contentTypeHeader
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      const blob = new Blob([response.data], {
        type: contentType
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Помилка експорту файлу';
      setExportError(typeof message === 'string' ? message : 'Помилка експорту файлу');
    }
  };

  const handlePurchaseList = () => {
    if (changedInAnotherTab || !contextReady) return;
    purchaseRequest.current?.abort();
    purchaseRequest.current = dispatch(fetchPurchaseList(
      from || to ? { from: from || undefined, to: to || undefined } : undefined
    ));
  };

  const handleImportFileChange = async (file: File | null, type = importType) => {
    const requestSequence = ++previewSequence.current;
    setImportFile(file);
    setImportResult(null);
    setImportPreview(null);
    if (!file) {
      setPreviewLoading(false);
      return;
    }

    setPreviewLoading(true);
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      if (requestSequence !== previewSequence.current) return;
      const sheetName = workbook.SheetNames[0];
      const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
      if (!sheetName || !sheet) {
        setImportPreview({ sheetName: '', headers: [], sampleRows: [], totalRows: 0, error: 'У файлі не знайдено аркушів для імпорту.' });
        return;
      }

      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as unknown[][];
      const nonEmptyRows = rows.filter(row => row.some(value => String(value ?? '').trim() !== ''));
      if (nonEmptyRows.length === 0) {
        setImportPreview({ sheetName, headers: [], sampleRows: [], totalRows: 0, error: 'Перший аркуш не містить даних.' });
        return;
      }

      const headers = nonEmptyRows[0].map((value, index) =>
        value == null || value === '' ? `Колонка ${index + 1}` : String(value));
      const dataRows = nonEmptyRows.slice(1);
      setImportPreview({
        sheetName,
        headers,
        sampleRows: dataRows.slice(0, 5),
        totalRows: dataRows.length,
      });

      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await api.post(`/import/preview/${type}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (requestSequence === previewSequence.current) {
          setImportPreview(previous => previous
            ? { ...previous, serverValidation: response.data }
            : previous);
        }
      } catch (error: any) {
        if (requestSequence === previewSequence.current) {
          const message = error?.response?.data?.message || 'Не вдалося перевірити файл на сервері.';
          setImportPreview(previous => previous
            ? { ...previous, serverValidationError: typeof message === 'string' ? message : 'Не вдалося перевірити файл на сервері.' }
            : previous);
        }
      }
    } catch (error) {
      if (requestSequence !== previewSequence.current) return;
      setImportPreview({
        sheetName: '',
        headers: [],
        sampleRows: [],
        totalRows: 0,
        error: error instanceof Error ? `Не вдалося прочитати файл: ${error.message}` : 'Не вдалося прочитати файл Excel.',
      });
    } finally {
      if (requestSequence === previewSequence.current) setPreviewLoading(false);
    }
  };

  const submitImportFile = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    setImporting(true);
    try {
      const res = await api.post(`/import/${importType}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
    } catch (err: any) {
      const body = err?.response?.data;
      if (body?.rolledBack && Array.isArray(body.errors)) {
        setImportResult(body);
        return;
      }
      const message =
        body?.message ||
        (typeof body === 'string' ? body : null) ||
        (body ? JSON.stringify(body) : null) ||
        err?.message ||
        'Помилка імпорту';
      setImportResult({ imported: 0, errors: [message] });
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!importFile || !importPreview || importPreview.error || importPreview.serverValidation?.invalidRows
      || previewLoading || importing) return;
    await submitImportFile(importFile);
  };

  const retrySingleImport = () => {
    if (!importFile || !importPreview || importPreview.error || importPreview.serverValidation?.invalidRows
      || previewLoading || importing) return;
    void submitImportFile(importFile);
  };

  const handlePackageImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (packageFiles.length === 0) return;
    const formData = new FormData();
    packageFiles.forEach((f) => formData.append('files', f));
    try {
      const res = await api.post('/import/files', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPackageResult(res.data);
    } catch (err: any) {
      if (err?.response?.data?.rolledBack && Array.isArray(err.response.data.errors)) {
        setPackageResult(err.response.data);
        return;
      }
      setPackageResult({ imported: 0, errors: [err?.response?.data?.message || err?.response?.data || 'Помилка пакетного імпорту'] });
    }
  };

  const handleImportDocs = async () => {
    try {
      const res = await api.post(`/import/docs?clearExisting=${clearBeforeDocsImport}`);
      setDocsResult(res.data);
    } catch (err: any) {
      if (err?.response?.data?.rolledBack && Array.isArray(err.response.data.errors)) {
        setDocsResult(err.response.data);
        return;
      }
      setDocsResult({ imported: 0, errors: [err?.response?.data?.message || err?.response?.data || 'Помилка імпорту з папки docs'] });
    }
  };

  const downloadImportErrorReport = (result: ImportResult) => {
    const quote = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows: string[][] = [['Файл', 'Тип', 'Помилка']];
    result.errors.forEach(error => rows.push(['', '', String(error)]));
    result.files?.forEach(file => file.errors.forEach(error => rows.push([file.fileName, file.type, String(error)])));
    if (rows.length === 1) return;

    const csv = `\uFEFF${rows.map(row => row.map(quote).join(';')).join('\r\n')}`;
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `import-errors-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const renderImportResult = (result: ImportResult | null, onSafeRetry?: () => void) => {
    if (!result) return null;
    const updated = result.updated ?? 0;
    const hasErrors = result.errors.length > 0 || !!result.files?.some(file => file.errors.length > 0);
    return (
      <div className={`crm-import-result ${hasErrors ? 'crm-import-result--error' : 'crm-import-result--success'}`} role={hasErrors ? 'alert' : 'status'}>
        {result.rolledBack && <div className="crm-import-result__headline">Імпорт скасовано через помилки: жодну зміну не збережено.</div>}
        <strong>Імпортовано: {result.imported}</strong>{updated > 0 ? <span>, Оновлено: {updated}</span> : null}
        {result.files && result.files.length > 0 && (
          <ul style={{ marginTop: '8px', marginBottom: 0 }}>
            {result.files.map((f, i) => (
              <li key={`${f.fileName}-${i}`}>
                {f.fileName}: +{f.imported} / ~{f.updated}
                {f.errors.length > 0 && (
                  <ul className="crm-import-result__errors">
                    {f.errors.map((error, errorIndex) => <li key={errorIndex}>{String(error)}</li>)}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
        {result.errors.length > 0 && (
          <ul className="crm-import-result__errors">
            {result.errors.map((err, i) => <li key={i}>{String(err)}</li>)}
          </ul>
        )}
        {hasErrors && (
          <div className="crm-import-result__actions">
            <button type="button" onClick={() => downloadImportErrorReport(result)}>Завантажити звіт помилок CSV</button>
            {result.rolledBack && onSafeRetry && (
              <button type="button" onClick={onSafeRetry} disabled={importing}>
                {importing ? 'Повторний імпорт...' : 'Повторити імпорт'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  if (changedInAnotherTab) {
    return <div role="alert">{OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE}</div>;
  }

  return (
    <div className="page-shell" style={{ padding: '24px', color: 'var(--crm-text)' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>Експорт / Імпорт</h2>

      <section style={sectionStyle}>
        <h3>Експорт Excel</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button onClick={() => handleExport('products/excel')} style={successButtonStyle}>📥 Продукти (Excel)</button>
          <button onClick={() => handleExport('semifinished/excel')} style={successButtonStyle}>📥 Напівфабрикати (Excel)</button>
          <button onClick={() => handleExport('dishes/excel')} style={successButtonStyle}>📥 Страви (Excel)</button>
          <button onClick={() => handleExport('dish-template/excel')} style={buttonStyle}>📥 Шаблон нової страви</button>
          <button onClick={() => handleExport('product-categories/excel')} style={neutralButtonStyle}>📥 Категорії продуктів (Excel)</button>
          <button onClick={() => handleExport('semifinished-categories/excel')} style={neutralButtonStyle}>📥 Категорії н/ф (Excel)</button>
          <button onClick={() => handleExport('dish-categories/excel')} style={neutralButtonStyle}>📥 Категорії страв (Excel)</button>
          <button onClick={() => handleExport('dishes/pdf')} style={buttonStyle}>📄 Страви (HTML/PDF)</button>
        </div>
        {exportError && (
          <div style={{ marginTop: '12px', color: 'var(--crm-danger)' }}>{exportError}</div>
        )}
      </section>

      <section style={sectionStyle}>
        <h3>Імпорт із папки docs (сервер)</h3>
        <p style={{ marginTop: 0, color: 'var(--crm-text-muted)' }}>
          Шукає Excel-файли у папці <code>docs</code> та автоматично визначає тип за назвою файлу
          (<em>products/товари</em>, <em>semifinished/напівфабрикати</em>, <em>dishes/страви</em>,
          <em> product-categories</em>, <em>semifinished-categories</em>, <em>dish-categories</em>).
        </p>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
            <input type="checkbox" checked={clearBeforeDocsImport} onChange={(e) => setClearBeforeDocsImport(e.target.checked)} />
            Очистити поточні дані перед імпортом
          </label>
          <button onClick={handleImportDocs} style={buttonStyle}>📂 Імпортувати з docs</button>
        </div>
        {renderImportResult(docsResult)}
      </section>

      <section style={sectionStyle}>
        <h3>Імпорт Excel (один файл)</h3>
        <form onSubmit={handleImport}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label>
              Тип:
              <select value={importType} disabled={importing} onChange={e => { const type = e.target.value as ImportType; setImportType(type); setImportResult(null); if (importFile) void handleImportFileChange(importFile, type); }} style={controlStyle}>
                <option value="products">Продукти</option>
                <option value="semifinished">Напівфабрикати</option>
                <option value="dishes">Страви</option>
                <option value="product-categories">Категорії продуктів</option>
                <option value="semifinished-categories">Категорії напівфабрикатів</option>
                <option value="dish-categories">Категорії страв</option>
                <option value="dish-template">Шаблон нової страви</option>
              </select>
            </label>
            <label>
              Файл Excel (.xlsx/.xlsm):
              <input type="file" accept=".xlsx,.xlsm" disabled={importing} onChange={e => { void handleImportFileChange(e.target.files?.[0] ?? null); }} required style={controlStyle} />
            </label>
            <button type="submit" disabled={importing || previewLoading || !importPreview || !!importPreview.error || importPreview.totalRows === 0 || !!importPreview.serverValidation?.invalidRows} style={{ ...buttonStyle, opacity: importing || previewLoading || !importPreview || !!importPreview.error || importPreview.totalRows === 0 || !!importPreview.serverValidation?.invalidRows ? 0.65 : 1 }}>
              {importing ? 'Імпорт...' : '📤 Імпортувати'}
            </button>
          </div>
          {(previewLoading || importPreview) && (
            <div style={{ marginTop: '12px', padding: '12px', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)' }}>
              {previewLoading ? <div>Читаємо файл і перевіряємо рядки на сервері...</div> : importPreview?.error ? (
                <div role="alert" style={{ color: 'var(--crm-danger)' }}>{importPreview.error}</div>
              ) : importPreview ? (
                <>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Попередній перегляд:</strong> аркуш «{importPreview.sheetName}», рядків даних: {importPreview.totalRows}. Показано до 5 рядків.
                    {importPreview.totalRows === 0 && <span> Немає рядків для імпорту.</span>}
                    {importPreview.headers.length > 8 && <span> Показано перші 8 колонок.</span>}
                  </div>
                  {importPreview.sampleRows.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="crm-table crm-import-preview-table">
                        <thead>
                          <tr>
                            {importPreview.headers.slice(0, 8).map((header, index) => (
                              <th key={`${header}-${index}`}>{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {importPreview.sampleRows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {importPreview.headers.slice(0, 8).map((_, columnIndex) => (
                                <td key={columnIndex}>
                                  {String(row[columnIndex] ?? '')}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--crm-text-muted)' }}>
                    {importPreview.serverValidation ? (
                      <>
                        Серверна перевірка: без зауважень — {importPreview.serverValidation.validRows}; з некоректними значеннями — {importPreview.serverValidation.invalidRows}.
                        {importPreview.serverValidation.invalidRows > 0 && (
                          <ul className="crm-import-result__errors">
                            {importPreview.serverValidation.errors.slice(0, 8).map((error, index) => <li key={index}>{error}</li>)}
                          </ul>
                        )}
                        {importPreview.serverValidation.errorsTruncated && <div>Показано лише перші помилки; завантажте файл і перевірте решту рядків.</div>}
                        {importPreview.serverValidation.invalidRows > 0 && <strong>Виправте помилки у файлі та виберіть його повторно, щоб продовжити.</strong>}
                        <div>Перевіряються структура аркушів і числові поля; зв’язки з наявними записами перевіряються під час імпорту.</div>
                      </>
                    ) : importPreview.serverValidationError ? (
                      <span role="alert" style={{ color: 'var(--crm-danger)' }}>{importPreview.serverValidationError} Імпорт усе ще можна запустити; сервер перевірить дані атомарно перед збереженням.</span>
                    ) : 'Попередній перегляд першого аркуша.'}
                  </div>
                </>
              ) : null}
            </div>
          )}
        </form>
        {renderImportResult(importResult, retrySingleImport)}
      </section>

      <section style={sectionStyle}>
        <h3>Пакетний імпорт (кілька файлів)</h3>
        <form onSubmit={handlePackageImport}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label>
              Файли Excel:
              <input
                type="file"
                multiple
                accept=".xlsx,.xlsm"
                onChange={(e) => setPackageFiles(Array.from(e.target.files || []))}
                style={controlStyle}
              />
            </label>
            <button type="submit" disabled={packageFiles.length === 0} style={neutralButtonStyle}>📦 Імпортувати пакет</button>
          </div>
          {packageFiles.length > 0 && (
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--crm-text-muted)' }}>
              Обрано файлів: {packageFiles.length}
            </div>
          )}
        </form>
        {renderImportResult(packageResult)}
      </section>

      <section style={sectionStyle}>
        <h3>Автоматична закупівля</h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '16px', flexWrap: 'wrap' }}>
          <label>Від: <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={controlStyle} /></label>
          <label>До: <input type="date" value={to} onChange={e => setTo(e.target.value)} style={controlStyle} /></label>
          <button onClick={handlePurchaseList} disabled={loading || changedInAnotherTab || !contextReady} style={buttonStyle}>
            {loading ? 'Розрахунок...' : 'Сформувати закупівлю'}
          </button>
        </div>
        {!contextReady ? (
          <div role={context.status === 'error' || context.status === 'missing' ? 'alert' : 'status'}>
            {context.error || (context.status === 'missing'
              ? 'Оберіть доступну робочу групу перед формуванням закупівлі.'
              : 'Підтвердження робочої групи…')}
          </div>
        ) : purchaseError ? (
          <div role="alert">{purchaseError}</div>
        ) : purchaseList && (
          <div>
            <p style={{ color: 'var(--crm-text-muted)' }}>Сформовано: {new Date(purchaseList.generatedAt).toLocaleString('uk-UA')}</p>
            <table className="crm-table" style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)' }}>
              <thead>
                <tr>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Продукт</th>
                  <th style={{ padding: '8px' }}>Постачальник</th>
                  <th style={{ padding: '8px' }}>Категорія</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>К-сть</th>
                  <th style={{ padding: '8px' }}>Тип</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Ціна</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Сума</th>
                </tr>
              </thead>
              <tbody>
                {purchaseList.items.map(item => (
                  <tr key={item.productId}>
                    <td style={{ padding: '8px' }}>{item.productName}</td>
                    <td style={{ padding: '8px' }}>{item.supplierName ?? '—'}</td>
                    <td style={{ padding: '8px' }}>{item.categoryName ?? '—'}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{item.requiredQuantity}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{item.weightType}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{item.unitPrice.toFixed(2)} ₴</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{item.totalCost.toFixed(2)} ₴</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="crm-import-result--success">
                  <td colSpan={6} style={{ padding: '10px', fontWeight: 'bold', textAlign: 'right' }}>Загалом:</td>
                  <td style={{ padding: '10px', fontWeight: 'bold', textAlign: 'right', color: 'var(--crm-success)' }}>{purchaseList.totalCost.toFixed(2)} ₴</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default ExportImportPage;
